import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import { supabase } from '../../../lib/supabase';
import QuotationPdfTemplate from './QuotationPdfTemplate';
import { buildWhatsAppMessage } from '../../../services/quotationFlowService';

const SavedQuotationDetail = ({ quotation, onBack, onEdit, onRefresh }) => {
  const [pdfMode, setPdfMode] = useState(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingOT, setGeneratingOT] = useState(false);
  const [authorizingQuote, setAuthorizingQuote] = useState(false);
  const [toast, setToast] = useState(null);
  const [showLinkOT, setShowLinkOT] = useState(false);
  const [otFolio, setOtFolio] = useState('');

  const isAuthorized = quotation?.status === 'aprobada';
  const isDirecta = quotation?.origin === 'directa';
  const items = Array.isArray(quotation?.items) ? quotation?.items : [];
  const total = quotation?.total || 0;

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSendWhatsApp = () => {
    const phone = quotation?.clientPhone || '';
    if (!phone?.trim()) {
      showToast('error', 'El cliente no tiene número de WhatsApp registrado.');
      return;
    }
    const cleanPhone = phone?.replace(/[\s\-().+]/g, '')?.replace(/[^0-9]/g, '');
    if (cleanPhone?.length < 7) {
      showToast('error', 'Número de WhatsApp inválido.');
      return;
    }
    setSendingWhatsApp(true);
    try {
      const message = buildWhatsAppMessage({
        folioCotizacion: quotation?.folio || '',
        folioRecepcion: quotation?.folio || '',
        clientName: quotation?.client || '',
        vehicle: {},
        quoteItems: items,
        total,
        authUrl: window?.location?.origin,
      });
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
      showToast('success', 'WhatsApp abierto correctamente.');
    } catch (err) {
      showToast('error', 'Error al abrir WhatsApp.');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendEmail = async () => {
    const email = quotation?.clientEmail || '';
    if (!email?.trim()) {
      showToast('error', 'El cliente no tiene email registrado.');
      return;
    }
    setSendingEmail(true);
    try {
      const subject = encodeURIComponent(`Cotización ${quotation?.folio}`);
      const body = encodeURIComponent(`Estimado cliente,\n\nAdjunto encontrará su cotización ${quotation?.folio}.\n\nTotal: $${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}\n\nGracias por su preferencia.`);
      window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      showToast('success', 'Cliente de correo abierto.');
    } catch (err) {
      showToast('error', 'Error al abrir el cliente de correo.');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkAsAuthorized = async () => {
    if (!quotation?.id) return;
    setAuthorizingQuote(true);
    try {
      const { error } = await supabase
        ?.from('quotations')
        ?.update({
          status: 'aprobada',
          approved_at: new Date()?.toISOString(),
        })
        ?.eq('id', quotation?.id);
      if (error) {
        showToast('error', 'Error al autorizar: ' + error?.message);
      } else {
        showToast('success', 'Cotización marcada como autorizada.');
        onRefresh?.();
      }
    } catch (err) {
      showToast('error', 'Error inesperado al autorizar.');
    } finally {
      setAuthorizingQuote(false);
    }
  };

  const handleLinkOT = async () => {
    if (!otFolio?.trim()) return;
    try {
      const { data: wo } = await supabase
        ?.from('work_orders')
        ?.select('id')
        ?.ilike('folio_ot', otFolio?.trim())
        ?.maybeSingle();
      if (!wo?.id) {
        showToast('error', 'No se encontró una OT con ese folio.');
        return;
      }
      const { error } = await supabase
        ?.from('quotations')
        ?.update({ work_order_id: wo?.id })
        ?.eq('id', quotation?.id);
      if (error) {
        showToast('error', 'Error al vincular OT: ' + error?.message);
      } else {
        showToast('success', 'OT vinculada correctamente.');
        setShowLinkOT(false);
        setOtFolio('');
        onRefresh?.();
      }
    } catch (err) {
      showToast('error', 'Error inesperado al vincular OT.');
    }
  };

  // Build a synthetic selectedWorkOrder for QuotationPdfTemplate
  const syntheticWorkOrder = {
    id: quotation?.work_order_id || quotation?.id,
    folio: quotation?.folio,
    vehicle: quotation?.vehicle,
    client: quotation?.client,
    vehicleData: {},
    clientData: { nombre: quotation?.client, telefono: quotation?.clientPhone || '' },
    companyId: quotation?.company_id,
    isDirectFlow: !quotation?.work_order_id,
  };

  const syntheticQuotation = {
    id: quotation?.id,
    folio_cotizacion: quotation?.folio,
    status: quotation?.status,
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`rounded-lg p-4 flex items-center gap-3 ${
          toast?.type === 'success' ?'bg-success/10 border border-success/20' :'bg-destructive/10 border border-destructive/20'
        }`}>
          <Icon
            name={toast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={20}
            className={toast?.type === 'success' ? 'text-success flex-shrink-0' : 'text-destructive flex-shrink-0'}
          />
          <p className={`text-sm font-body ${toast?.type === 'success' ? 'text-success' : 'text-destructive'}`}>
            {toast?.message}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={16} />
            Volver a Cotizaciones guardadas
          </button>
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h2 className="text-2xl font-heading font-bold text-foreground">{quotation?.folio}</h2>
              {isAuthorized ? (
                <span className="text-sm px-3 py-1 rounded-full bg-success/10 text-success font-semibold border border-success/20">
                  ✓ Autorizada
                </span>
              ) : (
                <span className="text-sm px-3 py-1 rounded-full bg-warning/10 text-warning font-semibold border border-warning/20">
                  Pendiente de autorización
                </span>
              )}
              {isDirecta && (
                <span className="text-sm px-3 py-1 rounded-full bg-accent/10 text-accent font-semibold border border-accent/20 flex items-center gap-1">
                  <Icon name="FilePlus" size={14} />
                  Directa
                </span>
              )}
            </div>
            <p className="text-base font-body text-foreground">{quotation?.vehicle}</p>
            <p className="text-sm text-muted-foreground">{quotation?.client}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Guardada: {quotation?.savedAt ? new Date(quotation?.savedAt)?.toLocaleDateString('es-MX', {
                day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
              }) : '—'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-heading font-bold text-foreground">
              ${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground">{items?.length} ítem{items?.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Zap" size={18} className="text-primary" />
          Acciones
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* Edit */}
          <button
            onClick={() => onEdit?.(quotation)}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-center"
          >
            <Icon name="Pencil" size={20} className="text-primary" />
            <span className="text-xs font-medium text-foreground">Editar</span>
          </button>

          {/* Print PDF */}
          <button
            onClick={() => setPdfMode('print')}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-center"
          >
            <Icon name="Printer" size={20} className="text-primary" />
            <span className="text-xs font-medium text-foreground">Imprimir PDF</span>
          </button>

          {/* Generate PDF */}
          <button
            onClick={() => setPdfMode('generate')}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-center"
          >
            <Icon name="FileDown" size={20} className="text-primary" />
            <span className="text-xs font-medium text-foreground">Generar PDF</span>
          </button>

          {/* Preview PDF */}
          <button
            onClick={() => setPdfMode('preview')}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-center"
          >
            <Icon name="Eye" size={20} className="text-primary" />
            <span className="text-xs font-medium text-foreground">Vista previa</span>
          </button>

          {/* WhatsApp */}
          <button
            onClick={handleSendWhatsApp}
            disabled={sendingWhatsApp}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-green-500/50 hover:bg-green-50 transition-colors text-center disabled:opacity-50"
          >
            <Icon name="MessageCircle" size={20} className="text-green-600" />
            <span className="text-xs font-medium text-foreground">WhatsApp</span>
          </button>

          {/* Email */}
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border hover:border-blue-500/50 hover:bg-blue-50 transition-colors text-center disabled:opacity-50"
          >
            <Icon name="Mail" size={20} className="text-blue-600" />
            <span className="text-xs font-medium text-foreground">Enviar Email</span>
          </button>

          {/* Authorize — only if not yet authorized */}
          {!isAuthorized ? (
            <button
              onClick={handleMarkAsAuthorized}
              disabled={authorizingQuote}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-warning/40 hover:border-warning hover:bg-warning/5 transition-colors text-center disabled:opacity-50"
            >
              {authorizingQuote ? (
                <Icon name="Loader2" size={20} className="text-warning animate-spin" />
              ) : (
                <Icon name="CheckCircle" size={20} className="text-warning" />
              )}
              <span className="text-xs font-medium text-foreground">Autorizar</span>
            </button>
          ) : (
            <div className="flex flex-col items-center gap-2 p-3 rounded-lg border border-success/30 bg-success/5 text-center">
              <Icon name="CheckCircle2" size={20} className="text-success" />
              <span className="text-xs font-medium text-success">Autorizada</span>
            </div>
          )}

          {/* Generate OT — only if authorized */}
          {isAuthorized ? (
            <button
              onClick={() => setShowLinkOT(true)}
              disabled={generatingOT || !!quotation?.work_order_id}
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-success/40 hover:border-success hover:bg-success/5 transition-colors text-center disabled:opacity-50"
            >
              <Icon name="ClipboardList" size={20} className="text-success" />
              <span className="text-xs font-medium text-foreground">
                {quotation?.work_order_id ? 'OT Vinculada' : 'Generar OT'}
              </span>
            </button>
          ) : (
            <div
              className="flex flex-col items-center gap-2 p-3 rounded-lg border border-border bg-muted/30 text-center cursor-not-allowed opacity-50"
              title="Solo disponible cuando la cotización está autorizada"
            >
              <Icon name="ClipboardList" size={20} className="text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Generar OT</span>
              <span className="text-[10px] text-muted-foreground leading-tight">Requiere autorización</span>
            </div>
          )}
        </div>

        {/* Link OT inline form */}
        {showLinkOT && isAuthorized && !quotation?.work_order_id && (
          <div className="mt-4 p-4 rounded-lg bg-success/5 border border-success/20 space-y-3">
            <p className="text-sm font-medium text-foreground">Vincular Orden de Trabajo</p>
            <p className="text-xs text-muted-foreground">Ingresa el folio de la OT existente para vincularla a esta cotización autorizada.</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Ej: OT-123456"
                value={otFolio}
                onChange={e => setOtFolio(e?.target?.value)}
                className="flex-1 text-sm border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-success"
              />
              <button
                onClick={handleLinkOT}
                disabled={!otFolio?.trim()}
                className="px-4 py-2 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50"
              >
                Vincular
              </button>
              <button
                onClick={() => { setShowLinkOT(false); setOtFolio(''); }}
                className="px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Items detail */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center gap-2">
          <Icon name="List" size={16} className="text-primary" />
          <h3 className="text-sm font-heading font-semibold text-foreground">Detalle de ítems</h3>
          <span className="ml-auto text-xs text-muted-foreground">{items?.length} ítem{items?.length !== 1 ? 's' : ''}</span>
        </div>
        {items?.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Sin ítems registrados.</div>
        ) : (
          <div className="divide-y divide-border">
            {items?.map((item, idx) => {
              const itemTotal = (item?.price || 0) * (item?.quantity || 1);
              const typeLabel = item?.type === 'labor' || item?.isLabor ? 'Mano de obra' : 'Refacción/Servicio';
              return (
                <div key={item?.id || idx} className="p-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        item?.type === 'labor' || item?.isLabor ?'bg-blue-50 text-blue-700' :'bg-muted text-muted-foreground'
                      }`}>
                        {typeLabel}
                      </span>
                      {item?.authorized && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success font-medium">
                          Autorizado
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-body text-foreground">{item?.description || '—'}</p>
                    {item?.notes && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item?.notes}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Cant: {item?.quantity || 1} × ${(item?.price || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-heading font-bold text-foreground">
                      ${itemTotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Total row */}
        <div className="px-4 py-3 bg-muted/30 border-t border-border flex items-center justify-between">
          <span className="text-sm font-heading font-semibold text-foreground">Total</span>
          <span className="text-lg font-heading font-bold text-foreground">
            ${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      {/* PDF Template */}
      {pdfMode && (
        <QuotationPdfTemplate
          selectedWorkOrder={syntheticWorkOrder}
          quoteItems={items}
          quotation={syntheticQuotation}
          signatureDataUrl={null}
          observations=""
          companyId={quotation?.company_id}
          mode={pdfMode}
          onClose={() => setPdfMode(null)}
        />
      )}
    </div>
  );
};

export default SavedQuotationDetail;
